/**
 * Panel — a titled Square/Mono surface, and DescriptionList — labelled facts.
 *
 * `panel()` in ../mono/primitives hands back a class string, which is the right
 * shape for a bare box but leaves every caller to rebuild the same header: a
 * mono eyebrow, a title, a right-aligned action cluster, a rule beneath. Panel
 * owns that header so the eyebrow/title/action relationship is decided once.
 *
 * DescriptionList replaces the 30 hand-rolled `<dl>` blocks. It is the right
 * element for label/value facts — screen readers pair the two, which a grid of
 * <div>s never does — and `columns` lets one component serve both the narrow
 * sidebar (1 column) and the wide summary header (3+).
 */
import React from 'react';
import { type Tone } from './foundation';

/* ── Panel ──────────────────────────────────────────────────────────────── */

export interface PanelProps {
  /** Mono eyebrow above the title. */
  label?: React.ReactNode;
  title?: React.ReactNode;
  /** Right-aligned cluster in the header — buttons, a status readout, a menu. */
  actions?: React.ReactNode;
  /** Small count beside the title, for a panel that holds a list. */
  count?: number;
  /** Drop the body padding — for a panel whose child is a full-bleed table or
      row list that must reach the panel's edges. */
  flush?: boolean;
  /** Quieter ground for a panel nested inside another panel. */
  inset?: boolean;
  /** Coloured left edge for semantic state. */
  accent?: Tone;
  children?: React.ReactNode;
  className?: string;
  /** Extra classes on the body wrapper only. */
  bodyClassName?: string;
}

export function Panel({
  label,
  title,
  actions,
  count,
  flush = false,
  inset = false,
  accent,
  children,
  className = '',
  bodyClassName = '',
}: PanelProps) {
  const hasHeader = Boolean(label || title || actions);
  return (
    <section
      className={`border ${inset ? 'bg-[var(--surface)]' : 'bg-[var(--bg-raised)]'} ${className}`}
      style={{
        borderColor: 'var(--rule)',
        ...(accent
          ? { boxShadow: `inset 3px 0 0 0 ${accent === 'neutral' ? 'var(--rule-bright)' : `var(--${accent})`}` }
          : {}),
      }}
    >
      {hasHeader && (
        <header
          className="flex items-center justify-between gap-4 border-b px-4 py-3"
          style={{ borderColor: 'var(--rule)' }}
        >
          <div className="min-w-0">
            {label && (
              <p className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
                {label}
              </p>
            )}
            {title && (
              <h2
                className={`font-serif text-lg leading-tight ${label ? 'mt-0.5' : ''}`}
                /* Inline colour is required: `.dark h1…h6` outweighs a plain
                   colour class. See ../mono/primitives. */
                style={{ color: 'var(--text-pri)' }}
              >
                {title}
                {typeof count === 'number' && (
                  <span className="ml-2 font-mono text-sm text-[var(--text-dim)]">{count}</span>
                )}
              </h2>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={flush ? bodyClassName : `p-4 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

/* ── DescriptionList ────────────────────────────────────────────────────── */

export interface DescriptionItem {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Span the full width regardless of `columns` — for a long value. */
  wide?: boolean;
}

export interface DescriptionListProps {
  items: DescriptionItem[];
  columns?: 1 | 2 | 3 | 4;
  /** `stacked` puts the label above the value; `inline` puts it to the left. */
  layout?: 'stacked' | 'inline';
  /** Hairline between rows — for a long list that needs scan lines. */
  divided?: boolean;
  className?: string;
}

const COLS: Record<1 | 2 | 3 | 4, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

export function DescriptionList({
  items,
  columns = 2,
  layout = 'stacked',
  divided = false,
  className = '',
}: DescriptionListProps) {
  return (
    <dl className={`grid gap-x-6 ${divided ? 'gap-y-0' : 'gap-y-4'} ${COLS[columns]} ${className}`}>
      {items.map((item, i) => (
        <div
          key={i}
          className={[
            item.wide ? 'col-span-full' : '',
            divided ? 'border-b py-2.5 last:border-b-0' : '',
            layout === 'inline' ? 'flex items-baseline gap-3' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={divided ? { borderColor: 'var(--rule)' } : undefined}
        >
          <dt
            className={`font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)] ${
              layout === 'inline' ? 'shrink-0' : ''
            }`}
          >
            {item.label}
          </dt>
          <dd className={`text-sm text-[var(--text-pri)] ${layout === 'stacked' ? 'mt-1' : ''}`}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
